import { Link } from "react-router-dom";
import { DirectorLinks, ActorLinks } from "@/components/DirectorLinks";
import { PlayCircle, BookmarkSimple, MusicNotes, Microphone, Star } from "@phosphor-icons/react";
import { formatDuration, formatViews, displayDuration } from "@/lib/api";
import { getMaturity, isMusic, isPodcast, isJustAdded, relativeAdded } from "@/lib/meta";
import { getProgress } from "@/lib/progress";
import { onImgError } from "@/lib/fallback";
import { useWatchLater } from "@/hooks/useWatchLater";
import { useHoverPreview } from "@/hooks/useHoverPreview";
import { HoverPreview } from "@/components/HoverPreview";

const cleanBlurb = (s) =>
  (s || "").split(/»|\bsubscribe\b/i)[0].replace(/https?:\/\/\S+/g, "").replace(/[#@]\w+/g, "").replace(/\s+/g, " ").trim();

// Desktop hover overlay: description + top cast (hidden on touch and TV via CSS).
const CardInfo = ({ video, previewing }) => {
  const blurb = cleanBlurb(video.description);
  const isSports = (video.keywords || []).some((k) => k === "sports" || k === "sports_highlight");
  const cast = isSports ? [] : (video.cast || []).filter(Boolean).slice(0, 3);
  const directors = isSports ? [] : video.directors || [];
  if (!blurb && !cast.length && !directors.length) return null;
  return (
    <div
      data-testid={`card-info-${video.video_id}`}
      className={`rv-card-info absolute inset-0 z-[15] hidden lg:flex flex-col justify-end gap-1.5 bg-gradient-to-t p-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${
        previewing ? "from-black/90 via-black/20 to-transparent" : "from-black/95 via-black/85 to-black/60"
      }`}
    >
      {blurb && <p className={`${previewing ? "line-clamp-2" : "line-clamp-4"} text-[12px] leading-snug text-white/85`}>{blurb}</p>}
      <ActorLinks names={cast} className="truncate font-mono text-[10px] uppercase tracking-[0.1em] text-amber/90" />
      <DirectorLinks names={directors} prefix="Dir. " className="truncate font-mono text-[10px] uppercase tracking-[0.1em] text-amber/90" />
    </div>
  );
};

export const VideoCard = ({ video, index = 0, datePill = false }) => {
  const { isSaved, toggle } = useWatchLater();
  const saved = isSaved(video.video_id);
  const maturity = getMaturity(video);
  const music = video.content_type === "music" || (!video.content_type && isMusic(video));
  const podcast = video.content_type === "podcast" || (!video.content_type && !music && isPodcast(video));
  const live = !!video.is_live;
  const justAdded = isJustAdded(video);
  const added = datePill ? relativeAdded(video.created_at) : null;
  const progress = getProgress(video.video_id);
  const pct = progress ? Math.min(100, Math.round((progress.timestamp / progress.duration) * 100)) : 0;
  const preview = useHoverPreview(video);
  return (
    <Link
      to={`/watch/${video.video_id}`}
      data-testid={`video-card-${video.video_id}`}
      tabIndex={0}
      onMouseEnter={preview.onEnter}
      onMouseLeave={preview.onLeave}
      className="rv-card group relative block rv-fade-up transition-transform duration-300 ease-out hover:z-20 hover:scale-[1.05] focus-visible:z-20 focus-visible:scale-[1.05] focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 focus:ring-offset-[#050505]"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >
      <div className="aspect-video relative overflow-hidden bg-ink-surface border border-white/10 group-hover:border-amber/60 transition-colors duration-300">
        <img
          src={video.default_thumb}
          alt={video.title}
          loading="lazy"
          onError={onImgError}
          className="h-full w-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        {preview.active && <HoverPreview src={preview.src} videoId={video.video_id} />}
        <div className={`absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 ${preview.active ? "" : "group-hover:opacity-100"}`}>
          <PlayCircle size={56} weight="duotone" className="text-amber drop-shadow-lg" />
        </div>
        <CardInfo video={video} previewing={preview.active} />
        {live ? (
          <span
            data-testid={`live-badge-${video.video_id}`}
            className="absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-crimson text-white text-[10px] font-black uppercase px-2 py-0.5 tracking-[0.15em] shadow-lg"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
          </span>
        ) : added ? (
          <span
            data-testid={`added-pill-${video.video_id}`}
            className="absolute top-2 left-2 z-10 bg-black/80 border border-amber/40 text-amber text-[10px] font-mono uppercase px-2 py-0.5 tracking-[0.12em]"
          >
            {added}
          </span>
        ) : (
          justAdded && (
            <span
              data-testid={`new-badge-${video.video_id}`}
              className="absolute top-2 left-2 z-10 bg-gradient-to-r from-amber to-crimson text-black text-[10px] font-black uppercase px-2 py-0.5 tracking-[0.15em] shadow-lg animate-pulse"
            >
              Just Added
            </span>
          )
        )}
        {live ? (
          <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-crimson px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
          </span>
        ) : music ? (
          <span
            data-testid={`music-badge-${video.video_id}`}
            className="absolute bottom-2 right-2 flex items-center gap-1 bg-amber/90 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-black"
          >
            <MusicNotes size={12} weight="fill" /> Music
          </span>
        ) : podcast ? (
          <span
            data-testid={`podcast-badge-${video.video_id}`}
            className="absolute bottom-2 right-2 flex items-center gap-1 bg-crimson/90 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-white"
          >
            <Microphone size={12} weight="fill" /> Podcast
          </span>
        ) : displayDuration(video) ? (
          <span data-testid={`duration-${video.video_id}`} className="absolute bottom-2 right-2 bg-black/80 px-2 py-0.5 font-mono text-[11px] tracking-[0.1em] text-white/90">
            {formatDuration(displayDuration(video))}
          </span>
        ) : null}
        {video.is_discovery && (
          <span className="absolute bottom-2 left-2 bg-crimson/90 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white">
            Trailer
          </span>
        )}
        {video.audio_format && (
          <span
            data-testid={`audio-badge-${video.video_id}`}
            className="absolute right-2 bottom-9 z-10 border border-sky-300/60 bg-black/80 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-sky-200"
          >
            {video.audio_format}
          </span>
        )}
        {video.indie_pick && (
          <span
            data-testid={`indie-pick-badge-${video.video_id}`}
            className={`absolute left-2 z-10 flex items-center gap-1 border border-amber/60 bg-black/80 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-amber shadow-lg ${video.is_discovery ? "bottom-8" : "bottom-2"}`}
          >
            <Star size={11} weight="fill" /> Indie Pick
          </span>
        )}
        <button
          data-testid={`bookmark-${video.video_id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle(video.video_id, { kids: (video.keywords || []).some((k) => String(k).toLowerCase() === "kids") });
          }}
          aria-label="Save for later"
          data-spatial-skip
          className={`absolute top-2 right-2 p-1.5 border transition-colors duration-300 ${
            saved
              ? "border-amber bg-black/70 text-amber"
              : "border-white/20 bg-black/50 text-white/70 opacity-100 sm:opacity-0 group-hover:opacity-100 hover:border-amber hover:text-amber"
          }`}
        >
          <BookmarkSimple size={18} weight={saved ? "fill" : "regular"} />
        </button>
        {pct > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50" data-testid={`progress-bar-${video.video_id}`}>
            <div className="h-full bg-amber" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="pt-2.5">
        <h3 className="font-serif text-base sm:text-lg leading-tight text-[#EDEDED] truncate group-hover:text-amber transition-colors duration-300">
          {video.title}
        </h3>
        <div className="mt-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-white/45 truncate">
          {live ? (
            <>
              <span className="flex items-center gap-1.5 text-crimson font-bold">
                <span className="h-1.5 w-1.5 rounded-full bg-crimson animate-pulse" /> Live Now
              </span>
              {video.channel_name && <span className="truncate text-white/40">{video.channel_name}</span>}
            </>
          ) : (
            <>
              <span data-testid={`free-badge-${video.video_id}`} className="text-emerald-400 font-bold">Free</span>
              <span className="text-crimson">•</span>
              {video.release_year && (
                <>
                  <span>{video.release_year}</span>
                  <span className="text-crimson">•</span>
                </>
              )}
              <span>{formatViews(video.views)} {video.views === 1 ? "view" : "views"}</span>
              <span
                data-testid={`maturity-badge-${video.video_id}`}
                className="ml-auto shrink-0 border border-white/20 px-1.5 py-0.5 text-white/60 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300"
              >
                {maturity}
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};
